import { useConnectionState } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, ButtonText, Center, HStack, Spinner, Text, VStack,
} from '@gluestack-ui/themed';

import { useDismissOpenMeetingConfirm } from './MeetingConfirm';

type MeetingConnectionState = {
  isReconnecting: boolean;
  isConnected: boolean;
};

const MeetingConnectionStateContext = createContext<MeetingConnectionState>({
  isReconnecting: false,
  // Fail-open outside MeetingConnectionGuard (isolated lobby tests).
  isConnected: true,
});

/** LiveKit reconnecting state for descendants inside MeetingConnectionGuard. */
export function useMeetingConnectionState(): MeetingConnectionState {
  return useContext(MeetingConnectionStateContext);
}

type MeetingConnectionGuardProps = {
  children: ReactNode;
  intentionalLeaveRef: MutableRefObject<boolean>;
  onRejoin?: () => void;
  /** Same leave path as the meeting control bar (disconnect + session onLeave). */
  onLeave?: () => void;
  /** Exam already finished — agent disconnect must not show Connection lost. */
  expectDisconnect?: boolean;
};

/**
 * LiveKit often emits Disconnected for a beat when another participant (the
 * examiner) joins or the signal reconnects, then returns to Connected /
 * Reconnecting. Treat that as lost immediately and the student sees Rejoin
 * after the examiner became ready.
 */
export const CONNECTION_LOST_GRACE_MS = 2_000;

/**
 * Shows reconnecting banner or connection-lost rejoin UI around meeting content.
 */
export function MeetingConnectionGuard({
  children,
  intentionalLeaveRef,
  onRejoin,
  onLeave,
  expectDisconnect = false,
}: MeetingConnectionGuardProps) {
  const { t } = useTranslation();
  const dismissOpenConfirm = useDismissOpenMeetingConfirm();
  const connectionState = useConnectionState();
  const wasConnectedRef = useRef(false);
  const [showLost, setShowLost] = useState(false);

  useEffect(() => {
    if (connectionState === ConnectionState.Connected) {
      wasConnectedRef.current = true;
      setShowLost(false);
    }
  }, [connectionState]);

  useEffect(() => {
    if (expectDisconnect) setShowLost(false);
  }, [expectDisconnect]);

  useEffect(() => {
    if (showLost) {
      dismissOpenConfirm();
    }
  }, [dismissOpenConfirm, showLost]);

  useEffect(() => {
    if (
      connectionState !== ConnectionState.Disconnected
      || !wasConnectedRef.current
      || intentionalLeaveRef.current
      || expectDisconnect
    ) {
      return undefined;
    }
    const timer = setTimeout(() => {
      dismissOpenConfirm();
      setShowLost(true);
    }, CONNECTION_LOST_GRACE_MS);
    return () => clearTimeout(timer);
  }, [connectionState, dismissOpenConfirm, intentionalLeaveRef, expectDisconnect]);

  const isReconnecting = connectionState === ConnectionState.Reconnecting
    || connectionState === ConnectionState.SignalReconnecting
    || (
      connectionState === ConnectionState.Disconnected
      && wasConnectedRef.current
      && !showLost
      && !expectDisconnect
      && !intentionalLeaveRef.current
    );

  const connectionStateValue = useMemo(
    () => ({
      isReconnecting,
      isConnected: connectionState === ConnectionState.Connected,
    }),
    [connectionState, isReconnecting],
  );

  if (showLost) {
    return (
      <Center flex={1} bg="$bgApp" p="$8" testID="meeting-connection-lost">
        <VStack space="lg" alignItems="center" maxWidth={400}>
          <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
            {t('meeting.connectionLostTitle')}
          </Text>
          <Text size="md" color="$textBody" textAlign="center">
            {t('meeting.connectionLostBody')}
          </Text>
          <HStack space="md" flexWrap="wrap" justifyContent="center">
            {onRejoin ? (
              <Button size="lg" action="primary" onPress={onRejoin} testID="meeting-connection-lost-rejoin">
                <ButtonText>{t('meeting.rejoin')}</ButtonText>
              </Button>
            ) : null}
            {onLeave ? (
              <Button
                size="lg"
                variant="outline"
                action="secondary"
                onPress={onLeave}
                testID="meeting-connection-lost-leave"
              >
                <ButtonText>{t('meeting.leave')}</ButtonText>
              </Button>
            ) : null}
          </HStack>
        </VStack>
      </Center>
    );
  }

  return (
    <MeetingConnectionStateContext.Provider value={connectionStateValue}>
      <Box flex={1} position="relative">
        {children}
        {isReconnecting ? (
          <Box
            position="absolute"
            top={12}
            left={0}
            right={0}
            alignItems="center"
            zIndex={20}
          >
            <Box
              bg="$bgDark"
              px="$4"
              py="$2"
              borderRadius="$full"
              flexDirection="row"
              alignItems="center"
              testID="meeting-reconnecting-banner"
            >
              <Spinner size="small" mr="$2" />
              <Text size="sm" color="$textInverted">
                {t('meeting.reconnecting')}
              </Text>
            </Box>
          </Box>
        ) : null}
      </Box>
    </MeetingConnectionStateContext.Provider>
  );
}
